// import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useMsal } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';
import {
    AutoComplete,
    Flex,
    Skeleton
} from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { getDataverseInstances, importMSPlannerTables } from '../../api/template/template';
import { PATHS } from "../../constants/PATHS";
// import { useAppDispatch, useAppSelector } from '../../store/hooks/hooks';
import { useUserData } from "../../hooks/useUserProfile";
import { Description } from '../Description';
import { ErrorView } from '../Error/ErrorView';
import { CustomButton } from "../FormFields/CustomButton";
import { acquireTokenWithConsent } from "../MSAL/acquireTokenWithConsent";
import { Title } from "../Title";

export const MSPlannerImport = () => {
    const { organizationId, tenantId } = useUserData();
    const [saving, setSaving] = useState(false);
    const autocompleteRef = useRef(null);
    const { instanceIdentifier } = useParams();
    const navigate = useNavigate();
    // const dispatch = useAppDispatch();
    const { instance } = useMsal();

    const { data, isError, error, isFetched, refetch } = useQuery({
        queryKey: ['dataverse instances'],
        queryFn: async () => {
            // wait for active account to be set
            await instance.getActiveAccount();
            const instanceToken = await acquireTokenWithConsent(instance, "https://globaldisco.crm.dynamics.com");

            const res = await getDataverseInstances({ "X-Ms-Access-Token": instanceToken });

            return res.data;
        },
        enabled: !!instance // Only run the query if we have an active account
    });

    useEffect(() => {
        if (autocompleteRef.current) {
            // @ts-ignore
            autocompleteRef.current?.focus();
        }
    }, [autocompleteRef, isFetched]);

    const items = data?.data?.instances.map((env: any) => ({
        label: env.friendlyName || env.url,
        value: env.url,
        envId: env.environmentId,
    }));

    const importHandler = async () => {
        if (!instanceIdentifier) {
            console.error("No instance identifier provided for import.");
            return;
        }
        // Implement the import logic here
        console.log("Importing data for instance:", instanceIdentifier);
        // You can dispatch an action or call an API to handle the import

        setSaving(true);

        const instanceUrl = items.find((item: any) => item.envId === instanceIdentifier)?.value
        const instanceToken = await acquireTokenWithConsent(instance, instanceUrl);

        importMSPlannerTables({
            organizationId: organizationId,
            tenantId: tenantId,
            AdapterIdentifier: "DataverseAdapter",
            OperationIdentifier: data?.data?.operationIdentifier,
            ImportConfigurationArguments: {
                InstanceUrl: instanceUrl
            }
        }, { "X-Ms-Access-Token": instanceToken })
            .then(() => {
                setSaving(false);
                toast.success("Import job started");
                navigate(`${PATHS.MSPLANNER}/${instanceIdentifier}/${data?.data?.operationIdentifier}`);
            })
            .catch((err) => {
                setSaving(false);
                console.error("Import failed:", err);
                toast.error("Import failed: " + (err.response?.data?.message || err.message));
            });
    }

    const picker = useMemo(() => (
        <AutoComplete
            popupMatchSelectWidth
            allowClear
            autoFocus
            value={items?.find((item: any) => item.envId === instanceIdentifier)?.label}
            options={items}
            placeholder="Select environment (instance)"
            filterOption={(inputValue, option) => {
                const regex = new RegExp(inputValue, "i");
                return regex.test(String(option?.value) ?? "");
            }}
            onSelect={(value, option) => {
                navigate(`${PATHS.MSPLANNER}/${option.envId}`);
            }}
            onClear={() => {
                navigate(`${PATHS.MSPLANNER}`);
            }}
            variant="outlined"
            style={{ minWidth: "350px" }}
            size="large"
            ref={autocompleteRef}
        />
    ), [items, instanceIdentifier]);

    if (isError) return (
        <Flex align="center" justify="center" style={{ width: "100%", height: "100%" }}>
            <ErrorView message="Failed to load Instances" error={error} showDetails />
            <button onClick={() => refetch()} style={{ marginTop: 20 }}>Retry</button>
        </Flex>
    );

    return (
        <Flex vertical align="center" justify="flex-start" gap={36} style={{ width: "100%", height: "100%" }} >
            <Title tagName="h1">Import Microsoft Planner Data</Title>
            <Flex vertical align="center" justify="center" gap={20} style={{ maxWidth: 600 }}>
                <Description theme="t3">
                    {/* eslint-disable-next-line max-len */}
                    Connect your Microsoft Planner workspace to automatically import your plans, tasks, and project data.
                    Select your Dataverse environment below to begin the import process.
                </Description>
                {isFetched
                    ? picker
                    : <Skeleton.Input size="large" style={{ width: 350 }} active />}
                <Description theme="t4">
                    This will import all plans, tasks, buckets, and assignments from your selected environment.
                </Description>
                <CustomButton
                    isDisabled={!isFetched || !instanceIdentifier}
                    onClick={importHandler}
                    isLoading={saving}
                >
                    Import tables
                </CustomButton>
            </Flex>
        </Flex>
    );
};
